import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectProjectDashboardData,
  selectProjectDashboardLoading,
  selectProjectDashboardError,
} from "@/Redux/Slices/projectDashboardSlice";
import { ContentLoader } from "@/Utility/Loader";
import NoDataFound from "@/Utility/NoDataFound/NoDataFound";
import Typography from "@/Utility/Typography";
import PendingTasks from "@/Pages/Dashboard/PendingTasks";
import ProjectStatistics from "./ProjectStatistics";
import Card from "@/Utility/Card/Card";
import { getProjectDashboardModel } from "./projectDashboard.mapper";
import { formatDate } from "@/utils/dateUtils";

function formatActivityTime(isoDate) {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour(s) ago`;
  if (diffDays < 7) return `${diffDays} day(s) ago`;
  return formatDate(d);
}

function DashboardPanel({ title, children, bodyClassName = "p-4 md:p-5" }) {
  return (
    <div className="flex flex-col bg-white border border-gray-200 shadow-lg card-border">
      <div className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center justify-between min-h-[48px]">
        <Typography variant="h4">{title}</Typography>
      </div>
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

const ProjectDashboard = ({ projectSlug }) => {
  const dashboardData = useSelector(selectProjectDashboardData);
  const loading = useSelector(selectProjectDashboardLoading);
  const error = useSelector(selectProjectDashboardError);

  const model = useMemo(() => getProjectDashboardModel(dashboardData), [dashboardData]);

  const statusChartData = model?.charts?.status ?? [];
  const priorityChartData = model?.charts?.priority ?? [];
  const typeChartData = model?.charts?.type ?? [];

  const activities = useMemo(
    () =>
      (model?.activity ?? []).map((a) => ({
        name: a.actorName,
        title: a.title,
        time: formatActivityTime(a.at),
      })),
    [model?.activity]
  );

  if (!projectSlug) {
    return <NoDataFound message="Select a project to view dashboard." />;
  }

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gray-50 rounded-lg border border-gray-100">
        <Typography variant="text1" className="text-red-600 text-lg">
          {error}
        </Typography>
      </div>
    );
  }

  if (loading) {
    return (
      <ContentLoader loading className="min-h-[calc(100vh-12rem)]">
        <div className="min-h-[50vh] w-full bg-gray-50 rounded-lg" aria-hidden />
      </ContentLoader>
    );
  }

  const cards = model?.cards ?? {};
  const hoursLimit = cards.hoursLimit;
  const usedHoursThisPeriod =
    model?.summary?.totalMinutes != null
      ? Math.round((Number(model.summary.totalMinutes) || 0) / 60)
      : null;
  const remainingHoursThisPeriod =
    hoursLimit == null || usedHoursThisPeriod == null
      ? null
      : Math.max(0, Number(hoursLimit) - Number(usedHoursThisPeriod));
  const percentUsedHours =
    hoursLimit == null || usedHoursThisPeriod == null || Number(hoursLimit) <= 0
      ? null
      : Math.round((Number(usedHoursThisPeriod) / Number(hoursLimit)) * 100);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card
            title="Total Time"
            value={cards.totalTime ?? "No data"}
            bgColor="bg-dashboard-variant-1"
            className="rounded-lg"
            icon="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
          <Card
            title="Due Soon"
            value={
              cards.dueSoonCount == null ? "No data" : Number(cards.dueSoonCount).toLocaleString()
            }
            bgColor="bg-dashboard-variant-2"
            className="rounded-lg"
            icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
          <Card
            title="Overdue"
            value={
              cards.overdueCount == null ? "No data" : Number(cards.overdueCount).toLocaleString()
            }
            bgColor="bg-dashboard-variant-3"
            className="rounded-lg"
            icon="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
          <Card
            title="Team Members"
            value={
              cards.totalMembers == null ? "No data" : Number(cards.totalMembers).toLocaleString()
            }
            bgColor="bg-dashboard-variant-4"
            className="rounded-lg"
            icon="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
          <Card
            title="Hours Limit"
            bgColor="bg-dashboard-variant-5"
            className="rounded-lg"
            additionalContent={
              <>
                <Typography
                  variant="text2"
                  className="text-white text-xs opacity-80 mt-0 flex gap-1 m-0 p-0"
                >
                  Budget:{" "}
                  <Typography variant="text2" className="text-white text-xs m-0 p-0 font-bold">
                    {hoursLimit == null ? "No data" : `${hoursLimit} hrs`}
                  </Typography>
                </Typography>
                <Typography
                  variant="text2"
                  className="text-white text-xs opacity-80 flex gap-1 m-0 p-0 mt-1 mb-1"
                >
                  Remaining:{" "}
                  <Typography variant="text2" className="text-white text-xs m-0 p-0 font-bold">
                    {remainingHoursThisPeriod == null
                      ? "No data"
                      : `${remainingHoursThisPeriod} hrs`}
                  </Typography>
                </Typography>
                <div className="flex items-center gap-3 mt-0">
                  <div className="flex-1 w-full bg-white bg-opacity-30 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        percentUsedHours != null && percentUsedHours > 100
                          ? "bg-red-500"
                          : percentUsedHours != null && percentUsedHours > 80
                            ? "bg-yellow-500"
                            : "bg-green-500"
                      }`}
                      style={{ width: `${Math.min(percentUsedHours ?? 0, 100)}%` }}
                    />
                  </div>
                  <Typography
                    variant="text2"
                    className="text-white text-xs font-medium whitespace-nowrap opacity-90 m-0"
                  >
                    {percentUsedHours == null ? "No data" : `${percentUsedHours}% used`}
                  </Typography>
                </div>
              </>
            }
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <DashboardPanel
            title="Status Overview"
            bodyClassName="p-4 md:p-5 max-h-[300px] overflow-y-auto"
          >
            <PendingTasks dataValues={statusChartData} />
          </DashboardPanel>
          <DashboardPanel
            title="Recent Activity"
            bodyClassName="px-4 py-4 space-y-5 max-h-[300px] overflow-y-auto"
          >
            {activities.length === 0 ? (
              <Typography variant="text2" className="text-gray-500">
                No recent activity
              </Typography>
            ) : (
              activities.map((item, index) => (
                <div key={index} className="flex gap-3 items-center">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                    {String(item.name).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 text-sm">
                    <Typography className="text-gray-800 leading-relaxed">
                      <Typography
                        variant="span3"
                        className="font-semibold text-green-600 mb-0"
                        component="span"
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        variant="span3"
                        className="text-gray-800 mb-0 pl-1"
                        component="span"
                      >
                        {item.title}
                      </Typography>
                      {" – "}
                      {item.time}
                    </Typography>
                  </div>
                </div>
              ))
            )}
          </DashboardPanel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
          <DashboardPanel title="Priority Breakdown">
            <ProjectStatistics dataValues={priorityChartData} />
          </DashboardPanel>
          <DashboardPanel title="Types Of Work" bodyClassName="p-4 md:p-5 h-auto min-h-0">
            <ProjectStatistics dataValues={typeChartData} />
          </DashboardPanel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
          <DashboardPanel title="Team" bodyClassName="p-4 md:p-5 space-y-3">
            {(model?.members ?? []).length === 0 ? (
              <Typography variant="text2" className="text-gray-500">
                No team members
              </Typography>
            ) : (
              (model.members ?? []).map((m) => (
                <div
                  key={m?.id ?? `${m?.email ?? ""}-${m?.name ?? ""}`}
                  className="flex items-center justify-between"
                >
                  <div>
                    <Typography className="font-semibold text-gray-800">
                      {m?.name ?? "—"}
                    </Typography>
                    <Typography variant="text2" className="text-gray-500">
                      {m?.email ?? ""}
                    </Typography>
                  </div>
                  <Typography variant="text2" className="text-gray-600">
                    {m?.role ?? ""}
                  </Typography>
                </div>
              ))
            )}
          </DashboardPanel>
          <DashboardPanel title="Type Breakdown">
            <PendingTasks dataValues={typeChartData} />
          </DashboardPanel>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;
